import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { settingsApi } from '../services/api';
import { withSuccessToast, successMessages } from '../utils/apiHelpers';
import { Spinner } from './ui/spinner';

interface CategoryCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCategoryCreated: (category: { _id: string; name: string }) => void;
  type: 'category' | 'subcategory';
  categoryId?: string; // Required for subcategory
}

/**
 * Modal for creating categories and subcategories inline
 * @param {CategoryCreateModalProps} props - Component props
 * @returns {JSX.Element} Category create modal
 */
export function CategoryCreateModal({ 
  isOpen, 
  onClose, 
  onCategoryCreated, 
  type,
  categoryId 
}: CategoryCreateModalProps) {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (type === 'subcategory' && !categoryId) {
      setError('Please select a category first');
      return;
    }

    setCreating(true);
    setError('');

    try {
      let response;
      if (type === 'category') {
        response = await withSuccessToast(
          settingsApi.createCategory({ name: name.trim() }),
          successMessages.categoryCreated
        );
        // New API structure: { data: {...}, success, message }
        onCategoryCreated(response.data.data);
      } else {
        response = await withSuccessToast(
          settingsApi.createSubcategory({ 
            category_id: categoryId!, 
            name: name.trim() 
          }),
          successMessages.subcategoryCreated
        );
        // New API structure: { data: {...}, success, message }
        onCategoryCreated(response.data.data);
      }
      
      setName('');
      onClose();
    } catch (error: any) {
      // Error toast will be shown automatically by API interceptor
      // Just set local error for form validation
      if (error.response?.status === 409) {
        setError(`This ${type} already exists`);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setName('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            Create New {type === 'category' ? 'Category' : 'Subcategory'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="name">
              {type === 'category' ? 'Category' : 'Subcategory'} Name
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError('');
              }}
              placeholder={`Enter ${type} name`}
              className="mt-1"
              disabled={creating}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !creating) {
                  handleCreate();
                }
              }}
            />
            {error && (
              <p className="text-sm text-destructive mt-1">{error}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleCreate}
            disabled={creating || !name.trim()}
          >
            {creating ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                Creating...
              </>
            ) : (
              'Create'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}